import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearch, useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { countTasks, useTasks, type ProcessStepAssignment } from '@/api/queries/useTasks';
import { useUserTombstoneLookup } from '@/api/queries/useOrgMembers';
import { useAuthStore } from '@/stores/auth-store';
import {
  type SubmitSurveyRequest,
  type SubmitVariantResponseRequest,
  type AssignmentProcess,
  type AssignmentProcessStepRecording
} from '@bliks-io/contracts';
import type { ReassignPayload } from '@/lib/successorEntityMapping';
import { PendingReviewsSection } from './components/PendingReviewsSection';
import { RecordingStartedCard } from './components/RecordingStartedCard';
import { TaskListStatus } from './components/TaskListStatus';
import { TaskModals } from './components/TaskModals';
import type { VariantAnswerOutcome } from '@/components/processes/SurveyDialog';
import { useRejectAssignment, useResolveUnclearAssignment } from '@/api/mutations/useAssignments';
import { toastApiError } from '@/api/mutations/toast-api-error';
import { useSurvey } from '@/api/queries/useSurveys';
import { useSubmitSurvey, useSubmitVariantResponse } from '@/api/mutations/useSurveys';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { readDialogLoadState } from '@/lib/dialogLoadState';
import type { AssignmentRow, ProcessStepInfo } from './types';
import {
  useOpenProcessesColumns,
  useInProgressRecordingsColumns,
  useAssignmentsColumns,
  usePendingSurveysColumns
} from './hooks/useMyTasksColumns';
import {
  getProcessStepInfo,
  getProcessStepSearchText,
  isUnresolvedStepId
} from './utils/processStepInfo';
import { handleReassignment } from './utils/reassignmentHandler';
import { useAutoOpenAssignment, useAutoOpenSurvey } from './hooks/useAutoOpenDialogs';
import { useSurveyFinishedCard } from './hooks/useSurveyFinishedCard';
import { getProcessId, getProcessName, getDepartment } from './utils/assignmentHelpers';
import { Page, PageContent, DataTable } from '@bliks-io/ui';

export function MyTasksPage() {
  const { t } = useTranslation();
  const slug = useAuthStore().slug();
  const navigate = useNavigate();
  const search = useSearch({ from: '/$slug/my-tasks/' });
  const { data: tasks, isLoading, isError, isFetching, refetch } = useTasks();
  const [selectedAssignment, setSelectedAssignment] = useState<ProcessStepAssignment | null>(null);
  const [selectedReassignment, setSelectedReassignment] = useState<ProcessStepAssignment | null>(
    null
  );
  const [selectedSurveyId, setSelectedSurveyId] = useState<string | null>(null);

  const rejectMutation = useRejectAssignment();
  const resolveUnclearMutation = useResolveUnclearAssignment();
  const surveyQuery = useSurvey(selectedSurveyId || '');
  const surveyData = surveyQuery.data;
  const submitSurvey = useSubmitSurvey();
  const submitVariantAnswer = useSubmitVariantResponse();

  // Check if sections have data
  const hasOpenProcesses = (tasks?.openProcesses?.length ?? 0) > 0;
  const hasInProgressRecordings = (tasks?.inProgressRecordings?.length ?? 0) > 0;
  const hasAssignments = (tasks?.assignments?.length ?? 0) > 0;
  // TanStack caches each row's accessor value until `data` changes identity, so
  // the translated predecessor text is resolved into the row itself.
  const assignmentRows = useMemo<AssignmentRow[]>(
    () =>
      (tasks?.assignments ?? []).map((assignment) => ({
        ...assignment,
        previousStepSearchText: isUnresolvedStepId(assignment.sourceProcessStep)
          ? ''
          : getProcessStepSearchText(
              getProcessStepInfo(assignment.sourceProcessStep, assignment.ruleIds, assignment, t)
            )
      })),
    [tasks?.assignments, t]
  );
  const hasPendingSurveys = (tasks?.pendingSurveys?.length ?? 0) > 0;

  // Counts every task section, including pending entity-suggestion reviews
  const taskCount = countTasks(tasks);

  useDocumentTitle('pageTitles.myTasksWithCount', { count: taskCount });

  const surveyFinishedCard = useSurveyFinishedCard({
    openSurveyId: selectedSurveyId,
    selectSurvey: setSelectedSurveyId
  });

  // Auto-open assignment from URL parameter
  useAutoOpenAssignment({
    assignmentId: search.assignmentId,
    assignments: tasks?.assignments,
    selectedAssignment,
    setSelectedAssignment,
    slug,
    search,
    navigate
  });

  // Auto-open survey from URL parameter. Whether the survey can be answered is
  // the dialog's call now, off the server toggles that ride with the row — a
  // stale link to a row nothing can be submitted to opens read-only, never a
  // form (ADR 0022), so no local toggle gate is needed here.
  useAutoOpenSurvey({
    surveyId: search.surveyId,
    selectedSurveyId,
    setSelectedSurveyId,
    slug,
    search,
    navigate
  });

  // Helper to get process step display information
  const getProcessStepInfoCallback = useCallback(
    (
      step:
        | string
        | ProcessStepInfo
        | AssignmentProcessStepRecording
        | 'START'
        | ProcessStepAssignment['sourceProcessStep'],
      ruleIds?: string[],
      assignment?:
        | ProcessStepAssignment
        | { process: string | AssignmentProcess | Record<string, unknown> }
    ) => getProcessStepInfo(step, ruleIds, assignment, t),
    [t]
  );

  // Handle reassignment submission for assignments
  const handleReassignSubmit = async (data: ReassignPayload) => {
    try {
      await handleReassignment(selectedReassignment, data, {
        rejectMutation,
        resolveUnclearMutation
      });
      // Close modal on success
      setSelectedReassignment(null);
    } catch (err) {
      console.error('Failed to reassign:', err);
      toastApiError(err, 'stepRecording.reassignError');
    }
  };

  const populatedRecording =
    surveyData && typeof surveyData.survey.stepRecording === 'object'
      ? surveyData.survey.stepRecording
      : undefined;

  // The dialog of an answered survey can still ask to close after another
  // survey was opened (SurveyDialog's save continuation).
  const closeSurvey = (surveyId: string | null) =>
    setSelectedSurveyId((current) => (current === surveyId ? null : current));

  // Handle survey submission
  const handleSurveySubmit = useCallback(
    async (response: SubmitSurveyRequest) => {
      if (!selectedSurveyId) return;

      try {
        await submitSurvey.mutateAsync({ surveyId: selectedSurveyId, data: response });
        surveyFinishedCard.celebrate(selectedSurveyId, tasks);
      } catch (error) {
        console.error('Failed to submit survey:', error);
        toastApiError(error, 'myTasks.surveySubmitFailed');
      }
    },
    [selectedSurveyId, submitSurvey, surveyFinishedCard, tasks]
  );

  /**
   * The Variant Check answer saves on its own endpoint (plan 029). What the
   * answer leaves is the dialog's call (`outcome`). This page reports the save:
   * a finished survey gets the survey-finished card, which closes the dialog in
   * the same tick it opens so the two never overlap; a decline, or an answer
   * that classic questions follow, gets the toast.
   */
  const handleVariantAnswer = useCallback(
    async (answer: SubmitVariantResponseRequest, outcome: VariantAnswerOutcome) => {
      // Throws rather than resolves: the dialog reads a resolution as "saved",
      // and would latch and close on an answer that never left the client.
      if (!selectedSurveyId || !populatedRecording) {
        throw new Error('No open survey with a populated step recording');
      }

      await submitVariantAnswer.mutateAsync({
        recordingId: populatedRecording._id,
        data: answer,
        // The answered row's process, for the read the decline answer revokes.
        processId: getProcessId(populatedRecording.process),
        surveyId: selectedSurveyId,
        endsSurvey: outcome !== 'continues'
      });

      if (outcome === 'finished') {
        surveyFinishedCard.celebrate(selectedSurveyId, tasks);
        return;
      }
      toast.success(t('myTasks.variantAnswerSaved'));
    },
    [populatedRecording, selectedSurveyId, surveyFinishedCard, submitVariantAnswer, t, tasks]
  );

  const userLookup = useUserTombstoneLookup();

  // Use extracted column hooks
  const openProcessesColumns = useOpenProcessesColumns(slug, userLookup);

  const inProgressRecordingsColumns = useInProgressRecordingsColumns({
    slug,
    getProcessName,
    getDepartment,
    getProcessStepInfo: getProcessStepInfoCallback
  });

  const assignmentsColumns = useAssignmentsColumns({
    slug,
    getProcessName,
    getDepartment,
    getProcessStepInfo: getProcessStepInfoCallback,
    setSelectedAssignment,
    setSelectedReassignment
  });

  const pendingSurveysColumns = usePendingSurveysColumns({
    slug,
    getProcessName,
    getDepartment,
    getProcessStepInfo: getProcessStepInfoCallback,
    onSurveyClick: setSelectedSurveyId
  });

  return (
    <Page>
      <PageContent className="space-y-8">
        {/* Page Header */}
        <div>
          <h1 className="text-xl font-semibold leading-7">{t('myTasks.title')}</h1>
          <p className="mt-1 text-sm font-normal leading-5 text-gray-8">
            {t('myTasks.description')}
          </p>
        </div>

        <TaskListStatus
          tasks={tasks}
          isLoading={isLoading}
          isError={isError}
          isFetching={isFetching}
          onRetry={refetch}
        />

        {/* Open Processes Section - only shown when has data */}
        {hasOpenProcesses && (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold leading-6">{t('myTasks.openProcessesTitle')}</h2>
              <p className="mt-1 text-sm font-normal leading-5 text-gray-8">
                {t('myTasks.openProcessesDescription')}
              </p>
            </div>
            <DataTable
              columns={openProcessesColumns}
              data={tasks?.openProcesses || []}
              emptyMessage={t('myTasks.noOpenProcesses')}
              enableGlobalFilter
              globalSearchPlaceholder={t('myTasks.searchOpenProcesses', {
                defaultValue: 'Search processes...'
              })}
              enableColumnVisibility
              enableColumnFilters
              enableUrlState
              tableId="openProcesses"
              initialSorting={[{ id: 'dueDate', desc: false }]}
            />
          </div>
        )}

        {/* In Progress Recordings Section - only shown when has data */}
        {hasInProgressRecordings && (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold leading-6">
                {t('myTasks.inProgressRecordingsTitle')}
              </h2>
              <p className="mt-1 text-sm font-normal leading-5 text-gray-8">
                {t('myTasks.inProgressRecordingsDescription')}
              </p>
            </div>
            <DataTable
              columns={inProgressRecordingsColumns}
              data={tasks?.inProgressRecordings || []}
              emptyMessage={t('myTasks.noInProgressRecordings')}
              enableGlobalFilter
              globalSearchPlaceholder={t('myTasks.searchInProgressRecordings', {
                defaultValue: 'Search recordings...'
              })}
              enableColumnVisibility
              enableColumnFilters
              enableUrlState
              tableId="inProgress"
              initialSorting={[{ id: 'dueDate', desc: false }]}
              initialGrouping={['processName']}
            />
          </div>
        )}

        {/* Assignments Section - only shown when has data */}
        {hasAssignments && (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold leading-6">{t('myTasks.assignmentsTitle')}</h2>
              <p className="mt-1 text-sm font-normal leading-5 text-gray-8">
                {t('myTasks.assignmentsDescription')}
              </p>
            </div>
            <DataTable
              columns={assignmentsColumns}
              data={assignmentRows}
              emptyMessage={t('myTasks.noAssignments')}
              enableGlobalFilter
              globalSearchPlaceholder={t('myTasks.searchAssignments', {
                defaultValue: 'Search assignments...'
              })}
              enableColumnVisibility
              enableColumnFilters
              enableUrlState
              tableId="assignments"
              initialSorting={[{ id: 'dueDate', desc: false }]}
              initialGrouping={['processName']}
            />
          </div>
        )}

        {/* Pending Surveys Section - only shown when has data */}
        {hasPendingSurveys && (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold leading-6">
                {t('myTasks.pendingSurveysTitle', { defaultValue: 'Pending Surveys' })}
              </h2>
              <p className="mt-1 text-sm font-normal leading-5 text-gray-8">
                {t('myTasks.pendingSurveysDescription', {
                  defaultValue: 'Provide feedback on your recorded process steps'
                })}
              </p>
            </div>
            <DataTable
              columns={pendingSurveysColumns}
              data={tasks?.pendingSurveys || []}
              emptyMessage={t('myTasks.noPendingSurveys', { defaultValue: 'No pending surveys' })}
              enableGlobalFilter
              globalSearchPlaceholder={t('myTasks.searchSurveys', {
                defaultValue: 'Search surveys...'
              })}
              enableColumnVisibility
              enableColumnFilters
              enableUrlState
              tableId="pendingSurveys"
              initialSorting={[{ id: 'dueDate', desc: false }]}
              initialGrouping={['processName']}
            />
          </div>
        )}

        <PendingReviewsSection />

        <TaskModals
          selectedAssignment={selectedAssignment}
          onAssignmentClose={() => setSelectedAssignment(null)}
          selectedReassignment={selectedReassignment}
          onReassignmentClose={() => setSelectedReassignment(null)}
          onReassignSubmit={handleReassignSubmit}
          isReassignmentSubmitting={rejectMutation.isPending || resolveUnclearMutation.isPending}
          selectedSurveyId={selectedSurveyId}
          surveyResponse={surveyData}
          surveyLoadState={readDialogLoadState([surveyQuery])}
          onSurveyClose={() => closeSurvey(selectedSurveyId)}
          onSurveySubmit={handleSurveySubmit}
          onVariantAnswer={handleVariantAnswer}
          isSurveySubmitting={submitSurvey.isPending || submitVariantAnswer.isPending}
        />

        <RecordingStartedCard />
      </PageContent>
      {/* Outside PageContent: as its last child, space-y-8 would add a gap below the last block. */}
      {surveyFinishedCard.element}
    </Page>
  );
}
